/**
 * AI先輩の画面で使う言い換え（issue #109）。
 *
 * 画面に出すのは**運用される方の言葉**だけにします（docs/06 原則5）。
 * `draft` `taught` のような内部の言い方は、ここで日本語に直してから出します。
 */
import type { AiKnowledgeItem, AiKnowledgeKind, AiKnowledgeStatus } from '../api/types.js';

/** 承認待ちの1件が「どこから来たか」。 */
export type PendingKind = 'taught' | 'question' | 'manual';

export function pendingKindLabel(kind: PendingKind): string {
  if (kind === 'taught') return '「AI先輩に教える」へのメッセージ';
  if (kind === 'question') return '答えられなかった質問';
  return '手動で登録した下書き';
}

/** その1件が、どこから来たものか。 */
export function pendingKindOf(entry: AiKnowledgeItem): PendingKind {
  if (!entry.source) return 'manual';
  return entry.source.origin === 'taught' ? 'taught' : 'question';
}

export function knowledgeKindLabel(kind: AiKnowledgeKind): string {
  return kind === 'doc' ? '公式資料' : 'Q&A';
}

/** いまの状態（言葉で出す・色だけで区別しない / docs/06 原則2）。 */
export function knowledgeStatusLabel(status: AiKnowledgeStatus): string {
  if (status === 'active') return '使われています';
  if (status === 'disabled') return '取り下げ済み';
  return '承認待ち';
}

/** その状態だと何が起きるか（1行の説明）。 */
export function knowledgeStatusNote(status: AiKnowledgeStatus): string {
  if (status === 'active') return 'AI先輩がこの内容を根拠に、出典つきでお答えします。';
  if (status === 'disabled') return '取り下げてあるので、AI先輩の答えには使われません。';
  return '承認するまで、AI先輩の答えには使われません。';
}

/** 一覧に並べるための短い抜き書き（長い本文はここで切る）。 */
export function summarize(text: string | null, max = 60): string {
  const body = (text ?? '').replace(/\s+/g, ' ').trim();
  if (body.length === 0) return '';
  return body.length > max ? `${body.slice(0, max)}…` : body;
}

/**
 * 答えられなかった質問の「どなた」の欄に出す言葉（issue #375）。
 * 答えを書くのにお名前は要らないので、どなたが・どのトークで聞いたかは出さない。
 * 労務・健康の相談が見分けをすり抜けても、個人と結びつかないようにするため。
 */
export const ASKER_NOT_SHOWN = 'お名前は出していません';

/**
 * 承認待ちの一覧で、答えられなかった質問の「どなた」の欄に出す印（issue #387）。
 * 毎行に「お名前は出していません」と書くと、狭い画面でこの欄が質問の欄を細くするので、
 * 欄は「—」にして、ページの説明に1回だけ書く（読み上げでは言葉で伝える）。
 */
export const ASKER_NOT_SHOWN_MARK = '—';

/** 承認待ちのページに1回だけ出す説明（issue #387）。 */
export const ASKER_NOT_SHOWN_NOTE =
  '答えられなかった質問は、どなたが・どのトークで聞いたかを出していません（一覧では「—」）。答えを書くのにお名前は要らないためです。';

/**
 * 「どなたが・どの部屋で」。分からないところは空けずに言葉で書く。
 * 他の拠点の方の投稿はお名前と部屋の名前が伏せてあるので、そう書く（issue #253）。
 *
 * 部屋の名前が空なのは1対1です。AI先輩への質問は AI先輩との1対1からしか届かないので、
 * そう書きます（「山田 太郎さん／」のように空けない・日本語監査 J-11）。
 */
export function whoAndWhere(
  userName: string | null,
  channelName: string | null,
  options: { fromOtherSite?: boolean } = {},
): string {
  if (options.fromOtherSite) return '他の拠点の方（お名前・トークの名前は伏せています）';
  const who = userName ? `${userName}さん` : 'どなたかは分かりません';
  const where =
    channelName === null
      ? 'トークは分かりません'
      : channelName.trim().length === 0
        ? 'AI先輩との1対1'
        : channelName;
  return `${who}／${where}`;
}

/**
 * 元の投稿の本文を画面に出す形（決定28）。
 * 元の投稿が消えた・取り消されたときは本文を消してあるので、なぜ無いのかを言葉で出す。
 */
export function sourceTextOf(entry: AiKnowledgeItem): string {
  const source = entry.source;
  if (!source) return '';
  if (source.text !== null) return source.text;
  if (source.text_erased === true) {
    return source.unsent === true
      ? '（送った方が送信取消したため、本文は残っていません）'
      : '（元のメッセージが保存期間を過ぎたかトークごと削除されて消えたため、本文は残っていません）';
  }
  return '（本文は残っていません）';
}

/** 文を消した行を一覧に出すときの言葉（決定28）。 */
export const ERASED_TEXT_LABEL = '（元のメッセージが消えたため、文は残っていません）';

/**
 * 元の投稿について、一覧・1件の画面で添えて伝えること（issue #255・#236・#245）。
 * 状態は言葉で出す（docs/06 原則2）。
 */
export function sourceNotes(entry: AiKnowledgeItem): string[] {
  const source = entry.source;
  if (!source || source.origin !== 'taught') return [];
  const notes: string[] = [];
  const attached = source.attachments ?? [];
  if (attached.length > 0) {
    const allPhotos = attached.every((item) => item.content_type.startsWith('image/'));
    notes.push(
      allPhotos ? `写真つき（${attached.length}枚）` : `写真・ファイルつき（${attached.length}件）`,
    );
  }
  if (source.unsent === true) {
    // 本文が消えているときは、本文の欄で「送信取消したため、本文は残っていません」と伝えているので重ねない（#324 L-16）
    if (source.text_erased !== true) notes.push('送った方が送信取消しました');
  } else if (source.acknowledged === false) {
    notes.push('メッセージを送った方へ「受け取りました」の返事がまだ届いていません');
  }
  return notes;
}
